import React, { useState } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from 'recharts'
import {
  SYNTHETIC_SPORT_PERFORMANCE,
} from './syntheticDashboardData'
import {
  Zap,
  Shield,
  ArrowRight,
} from 'lucide-react'

interface TeamSportPerformanceViewProps {
  onNavigateToStats?: () => void
}

export const SyntheticSportPerformanceView: React.FC<TeamSportPerformanceViewProps> = ({
  onNavigateToStats,
}) => {
  const [selectedUnit, setSelectedUnit] = useState<'offense' | 'defense'>('offense')
  const stats = SYNTHETIC_SPORT_PERFORMANCE

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Unit Selector & Context Strip */}
      <div style={{
        background: 'var(--surface-card, #ffffff)',
        border: '1px solid var(--surface-border, #e2e8f0)',
        borderRadius: '10px',
        padding: '0.9rem 1.25rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '0.75rem',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              background: 'var(--brand-red, #b91c1c)',
              color: '#ffffff',
              fontSize: '0.7rem',
              fontWeight: 800,
              padding: '2px 8px',
              borderRadius: '4px',
              textTransform: 'uppercase',
            }}>
              M8C · Rendimiento Deportivo
            </span>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted, #64748b)' }}>
              Ciclo preparatorio 2026 · Partidos y Scrimmages
            </span>
          </div>
          <h2 style={{
            margin: '4px 0 0 0',
            fontSize: '1.25rem',
            fontWeight: 800,
            color: 'var(--text-main, #0f172a)',
            letterSpacing: '-0.02em',
          }}>
            Análisis Táctico y Estadísticas Colectivas (Flag 5v5)
          </h2>
        </div>

        {/* Unit Toggle */}
        <div style={{
          display: 'inline-flex',
          background: 'var(--surface-subtle, #f8fafc)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '8px',
          padding: '3px',
        }}>
          <button
            type="button"
            onClick={() => setSelectedUnit('offense')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '6px',
              border: 'none',
              background: selectedUnit === 'offense' ? 'var(--nav-bg-base, #0a1320)' : 'transparent',
              color: selectedUnit === 'offense' ? '#ffffff' : 'var(--text-secondary, #475569)',
              fontSize: '0.82rem',
              fontWeight: selectedUnit === 'offense' ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <Zap size={14} color={selectedUnit === 'offense' ? '#f59e0b' : '#64748b'} />
            <span>Unidad de Ataque</span>
          </button>
          <button
            type="button"
            onClick={() => setSelectedUnit('defense')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '6px',
              border: 'none',
              background: selectedUnit === 'defense' ? 'var(--nav-bg-base, #0a1320)' : 'transparent',
              color: selectedUnit === 'defense' ? '#ffffff' : 'var(--text-secondary, #475569)',
              fontSize: '0.82rem',
              fontWeight: selectedUnit === 'defense' ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <Shield size={14} color={selectedUnit === 'defense' ? '#3b82f6' : '#64748b'} />
            <span>Unidad de Defensa</span>
          </button>
        </div>
      </div>

      {/* KPI Row (Dynamic based on selected unit) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '0.85rem',
      }}>
        {selectedUnit === 'offense' ? (
          <>
            {/* Offense KPI 1: Snaps Válidos */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Snaps Ofensivos
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                {stats.offense.validSnaps} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>snaps</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                4 partidos + 2 scrimmages
              </div>
            </div>

            {/* Offense KPI 2: Yardas netas por snap */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Ganancia Neta / Snap
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>
                {stats.offense.yardsPerSnap} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>yd / jugada</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {stats.offense.netYards} yardas netas totales
              </div>
            </div>

            {/* Offense KPI 3: Pases Completos / Intentos */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Completos / Intentos
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                {stats.offense.completionPct}%
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {stats.offense.passCompletions} / {stats.offense.passAttempts} ({stats.offense.passYards} yd pase)
              </div>
            </div>

            {/* Offense KPI 4: Touchdowns */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Touchdowns Anotados
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#b91c1c', fontVariantNumeric: 'tabular-nums' }}>
                {stats.offense.totalTouchdowns} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>TD</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {stats.offense.passTouchdowns} pase · {stats.offense.rushTouchdowns} carrera
              </div>
            </div>

            {/* Offense KPI 5: Pérdidas de Balón */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Pérdidas de Balón (INT)
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#d97706', fontVariantNumeric: 'tabular-nums' }}>
                {stats.offense.turnoversLost} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>turnovers</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                3.57% tasa de intercepción / pase
              </div>
            </div>

            {/* Offense KPI 6: Jugadas Explosivas (10+ yd) */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Jugadas ≥ 10 Yardas
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0284c7', fontVariantNumeric: 'tabular-nums' }}>
                {stats.offense.explosivePlaysPct}%
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {stats.offense.explosivePlays10Plus} jugadas explosivas
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Defense KPI 1: Snaps Defensivos */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Snaps Defensivos
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                {stats.defense.validSnaps} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>snaps</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                Oposición en 6 jornadas
              </div>
            </div>

            {/* Defense KPI 2: Yardas Permitidas / Snap */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Yardas Permitidas / Snap
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>
                {stats.defense.yardsAllowedPerSnap} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>yd / jugada</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {stats.defense.yardsAllowed} yardas permitidas totales
              </div>
            </div>

            {/* Defense KPI 3: Efectividad de Flags */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Efectividad de Flags
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                {stats.defense.flagTacklePct}%
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {stats.defense.flagsMade} conseguidos / {stats.defense.flagAttempts} intentos
              </div>
            </div>

            {/* Defense KPI 4: Recuperaciones Defensivas */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Turnovers Forzados
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#b91c1c', fontVariantNumeric: 'tabular-nums' }}>
                {stats.defense.turnoversForced} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>recup.</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {stats.defense.interceptionsMade} INT · {stats.defense.turnoverOnDowns} por downs
              </div>
            </div>

            {/* Defense KPI 5: Presiones y Sacks */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Presión de Blitz
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#d97706', fontVariantNumeric: 'tabular-nums' }}>
                {stats.defense.blitzDisruptionPct}%
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {stats.defense.blitzPressures} presiones + {stats.defense.blitzSacks} sacks en {stats.defense.blitzSnaps} blitzes
              </div>
            </div>

            {/* Defense KPI 6: Touchdowns Permitidos */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                TD Permitidos
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
                {stats.defense.touchdownsAllowed} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>TD</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                2.33 TD permitidos / jornada
              </div>
            </div>
          </>
        )}
      </div>

      {/* Main Charts Row: Match Evolution & Yard Distribution */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        gap: '1.25rem',
      }}>
        {/* Chart 1: Evolución por Partido / Scrimmage */}
        <div style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '10px',
          padding: '1.25rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
                Evolución de Ganancia Neta por Jornada
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: 'var(--text-secondary, #475569)' }}>
                Promedio de yardas por snap en partidos oficiales y scrimmages guiados (indicados explícitamente)
              </p>
            </div>
          </div>

          <div style={{ width: '100%', height: '220px', marginTop: '0.5rem' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.games} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 8]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: '#0a1320', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '0.75rem' }}
                  formatter={(val: any, _name: any, item: any) => [
                    `${val} yd/snap (${item.payload.snaps} snaps, Marcador: ${item.payload.ourScore}-${item.payload.oppScore})`,
                    `${item.payload.opponent} · ${item.payload.type === 'scrimmage_guiado' ? 'Scrimmage Guiado' : 'Partido Oficial'}`,
                  ]}
                />
                <Bar dataKey="yardsPerSnap" radius={[4, 4, 0, 0]}>
                  {stats.games.map((g) => (
                    <Cell
                      key={g.id}
                      fill={g.type === 'scrimmage_guiado' ? '#94a3b8' : 'var(--brand-red, #b91c1c)'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ display: 'flex', gap: '12px', fontSize: '0.72rem', color: '#64748b', marginTop: '6px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: 'var(--brand-red, #b91c1c)' }} />
              Partido Oficial
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#94a3b8' }} />
              Scrimmage Guiado (Práctica)
            </span>
          </div>
        </div>

        {/* Chart 2: Distribución de Ganancias (Histograma) */}
        <div style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '10px',
          padding: '1.25rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
                Distribución de Ganancia en Yardas
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: 'var(--text-secondary, #475569)' }}>
                Frecuencia real de ganancias por tramos (desglose cuantitativo, no solo promedio)
              </p>
            </div>
          </div>

          <div style={{ width: '100%', height: '220px', marginTop: '0.5rem' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.yardDistribution} layout="vertical" margin={{ top: 10, right: 20, left: 20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis dataKey="range" type="category" tick={{ fontSize: 11, fill: '#0f172a', fontWeight: 600 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: '#0a1320', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '0.75rem' }}
                  formatter={(val: any, _name: any, item: any) => [
                    `${val} jugadas (${item.payload.percentage}%)`,
                    item.payload.label,
                  ]}
                />
                <Bar dataKey="count" fill="var(--nav-bg-base, #0a1320)" radius={[0, 4, 4, 0]}>
                  {stats.yardDistribution.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.range === '<0 yd' ? '#b91c1c' : entry.range === '20+ yd' ? '#15803d' : '#0a1320'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Tactic Concepts or Defensive Calls Breakdown Table */}
      <div style={{
        background: 'var(--surface-card, #ffffff)',
        border: '1px solid var(--surface-border, #e2e8f0)',
        borderRadius: '10px',
        padding: '1.25rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
              {selectedUnit === 'offense' ? 'Rendimiento por Concepto Ofensivo' : 'Efectividad por Cobertura Defensiva'}
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.78rem', color: 'var(--text-secondary, #475569)' }}>
              {selectedUnit === 'offense'
                ? 'Comportamiento táctico en jugadas de catálogo Base, Dinámico, Hydra y Standard'
                : 'Métricas de parada y yardas permitidas según la llamada defensiva de catálogo'}
            </p>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', textAlign: 'left' }}>
            <thead style={{ background: 'var(--surface-subtle, #f8fafc)', borderBottom: '1px solid #e2e8f0' }}>
              <tr>
                <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b' }}>
                  {selectedUnit === 'offense' ? 'Concepto' : 'Llamada Defensiva'}
                </th>
                <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b', textAlign: 'right' }}>Snaps (n)</th>
                <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b', textAlign: 'right' }}>
                  {selectedUnit === 'offense' ? 'Yd / Snap' : 'Yd Perm / Snap'}
                </th>
                <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b', textAlign: 'right' }}>
                  {selectedUnit === 'offense' ? '% Completos' : 'Intercepciones'}
                </th>
                <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b', textAlign: 'right' }}>
                  {selectedUnit === 'offense' ? 'Touchdowns' : 'Sacks'}
                </th>
                <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b', textAlign: 'right' }}>
                  {selectedUnit === 'offense' ? 'Tasa de Éxito (≥4yd)' : 'Tasa de Parada (<4yd)'}
                </th>
              </tr>
            </thead>
            <tbody>
              {selectedUnit === 'offense' ? (
                stats.conceptsOffense.map((c) => (
                  <tr key={c.concept} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 700, color: '#0f172a' }}>{c.concept}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.snaps}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>{c.yardsPerSnap} yd</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.completionPct}%</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.touchdowns} TD</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#0284c7', fontVariantNumeric: 'tabular-nums' }}>{c.successRate}%</td>
                  </tr>
                ))
              ) : (
                stats.callsDefense.map((d) => (
                  <tr key={d.coverageCall} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 700, color: '#0f172a' }}>{d.coverageCall}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{d.snaps}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{d.yardsPerSnapAllowed} yd</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{d.interceptions} INT</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{d.sacks}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>{d.stopRate}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lineup Combinations with Sample Sizes */}
      <div style={{
        background: 'var(--surface-card, #ffffff)',
        border: '1px solid var(--surface-border, #e2e8f0)',
        borderRadius: '10px',
        padding: '1.25rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
              Quintetos Destacados por Rendimiento y Muestra en Campo
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.78rem', color: 'var(--text-secondary, #475569)' }}>
              Agrupaciones reales de 5 atletas con muestra visible (mínimo 20 snaps registrados)
            </p>
          </div>

          {onNavigateToStats && (
            <button
              type="button"
              onClick={onNavigateToStats}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '0.75rem',
                fontWeight: 700,
                color: 'var(--brand-red, #b91c1c)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <span>Ver todas las combinaciones en M7</span>
              <ArrowRight size={12} />
            </button>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {(selectedUnit === 'offense' ? stats.lineupsOffense : stats.lineupsDefense).map((lineup) => (
            <div
              key={lineup.id}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                background: '#f8fafc',
              }}
            >
              <div>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>
                  {lineup.players}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                  Muestra: <strong>{lineup.snaps} snaps</strong> en competición
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                    {selectedUnit === 'offense' ? 'Yardas / Snap' : 'Yd Perm / Snap'}
                  </div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 800, color: selectedUnit === 'offense' ? '#15803d' : '#0f172a' }}>
                    {selectedUnit === 'offense' ? `${(lineup as any).yardsPerSnap} yd` : `${(lineup as any).yardsPerSnapAllowed} yd`}
                  </div>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                    {selectedUnit === 'offense' ? '% Completos' : 'Intercepciones'}
                  </div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>
                    {selectedUnit === 'offense' ? `${(lineup as any).completionPct}%` : `${(lineup as any).interceptions} INT`}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
